import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { QA_STATES } from './qaStateMachine';

/**
 * Q&A session status, sized to sit in the bottom control bar.
 *
 * This used to be a ~160px tall column panel, which was right when the call
 * controls lived in a vertical SidePanel. The Teams-style layout replaced that
 * with a 60px horizontal BottomBar, and a column panel dropped into `.bar-right`
 * overflowed the bar entirely — including, during AWAITING_ANSWER, the "Next"
 * button, which rendered outside the container and could not be clicked.
 *
 * So it is now a single horizontal strip. What was dropped in the process was
 * dropped because the tiles already say it better:
 *   - WHO is asking, and whether they are asking or waiting, is the tile glow
 *     (bright/soft) in tileGlow.js.
 *   - The question TEXT is the caption inside the lit tile.
 *   - The 3-2-1 pre-roll is CountdownOverlay, full-stage, in CallTiles.styles.
 * What is kept is what nothing else shows: which question you are on, overall
 * progress, the skipped count, and the manual "Next" control.
 */

const pulse = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.12); opacity: 0.65; }
    100% { transform: scale(1); opacity: 1; }
`;

const wave = keyframes`
    0%, 100% { height: 6px; }
    50% { height: 16px; }
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    height: 44px;
    padding: 0 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: white;
    font-family: Montserrat, sans-serif;
    /* Capped so it can never crowd the absolutely-centred button cluster. */
    max-width: 440px;
    min-width: 0;
`;

const QuestionNumber = styled.div`
    font-size: 0.95rem;
    font-weight: 700;
    color: #00d9ff;
    white-space: nowrap;
    flex-shrink: 0;
    /* The call controls are absolutely centred and paint above this strip, so
       on a narrow or zoomed window the strip could slide under them (review,
       2026-09-04). Give up the counter first; the lit tile already shows the
       question. */
    @media (max-width: 1100px) {
        display: none;
    }
`;

const TotalQuestions = styled.span`
    font-size: 0.8rem;
    color: #8a8f98;
    margin-left: 2px;
`;

const ProgressBar = styled.div`
    width: 56px;
    height: 4px;
    background: #3a3d44;
    border-radius: 2px;
    overflow: hidden;
    flex-shrink: 0;
`;

const ProgressFill = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #00d9ff, #00ff88);
    border-radius: 2px;
    transition: width 0.3s ease;
    width: ${props => props.$progress}%;
`;

const Status = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const StatusDot = styled.div`
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 0.7rem;
    flex-shrink: 0;
    background: ${props => props.$bgColor || '#3a3d44'};
    animation: ${props => (props.$animate ? pulse : 'none')} 1.5s ease-in-out infinite;
`;

const StatusLabel = styled.div`
    font-size: 0.8rem;
    font-weight: 600;
    color: ${props => props.$color || 'rgba(255,255,255,0.9)'};
    white-space: nowrap;
    /* Never the element that gives way: it was the only shrinkable item in
       the strip, so a tight bar rendered "Recording..." as "R" (Toby,
       2026-09-03). The decorative progress bar steps aside instead. */
    flex-shrink: 0;
`;

const AudioWaves = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    height: 22px;
    flex-shrink: 0;
`;

const AudioBar = styled.div`
    width: 3px;
    background: #00ff88;
    border-radius: 2px;
    animation: ${wave} 0.8s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
`;

// The button is the rep's ONLY pacing control now (manual advance), so it has
// to read as the obvious next action at a glance: filled accent instead of a
// ghost outline, larger, and a soft pulse while an answer is being recorded
// (Toby, 2026-09-03: "needs to be a bit more obvious").
const glow = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(0, 217, 255, 0.55); }
    50% { box-shadow: 0 0 0 7px rgba(0, 217, 255, 0); }
`;

const NextQuestionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: #00d9ff;
    border: 1px solid #00d9ff;
    border-radius: 8px;
    color: #0b0f14;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    font-family: Montserrat, sans-serif;
    animation: ${glow} 1.6s ease-out infinite;
    transition: background 0.2s ease, transform 0.1s ease;

    &:hover {
        background: #5fe8ff;
    }
    &:active {
        transform: scale(0.98);
    }

    kbd {
        font-family: inherit;
        font-size: 0.65rem;
        font-weight: 600;
        padding: 1px 5px;
        border-radius: 4px;
        background: rgba(11, 15, 20, 0.18);
        color: #0b0f14;
    }
    /* Second thing to give up on a narrow window (see QuestionNumber). */
    @media (max-width: 1100px) {
        kbd { display: none; }
    }
`;

const SkippedBadge = styled.span`
    padding: 2px 8px;
    background: #ff6b6b;
    color: white;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
`;

/**
 * QAProgressIndicator - Q&A session progress and state, as a bar-height strip.
 *
 * @param {Object} props
 * @param {string} props.state - Current QA_STATE
 * @param {number} props.currentQuestion - Current question number (1-indexed)
 * @param {number} props.totalQuestions - Total number of questions
 * @param {number} props.skippedCount - Number of skipped questions
 * @param {number} props.countdown - Countdown value (optional, for COUNTDOWN state)
 * @param {string} props.participantName - Name of the Murphy participant asking
 * @param {Function} props.onNextQuestion - Callback to manually advance
 */
const QAProgressIndicator = ({
    state,
    currentQuestion,
    totalQuestions,
    skippedCount = 0,
    countdown = 3,
    participantName = 'Murphy',
    onNextQuestion
}) => {
    const progress = state === QA_STATES.COMPLETE
        ? 100
        : (totalQuestions > 0 ? ((currentQuestion - 1) / totalQuestions) * 100 : 0);

    // Keyboard advance: Enter does what the button does while an answer is
    // being recorded. Ignored when focus is on a control (a focused button's
    // own Enter-click already fires, so this would double it) and on key
    // repeat. The effect unmounts with the state, so a press during the
    // brief processing pause cannot advance twice.
    useEffect(() => {
        if (state !== QA_STATES.AWAITING_ANSWER || !onNextQuestion) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Enter' || e.repeat) return;
            const tag = e.target && e.target.tagName;
            if (['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
            // A modal owns Enter while it is up (the leave-page prompt renders
            // while recording, so this strip is still mounted underneath it).
            // Without this, answering that prompt advanced the session and, on
            // the last question, submitted it (review, 2026-09-04).
            if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
            e.preventDefault();
            onNextQuestion();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [state, onNextQuestion]);

    // One row per state: a dot (or the audio waves) plus a single line of text.
    // The tile glow already says who is talking, so this only has to say what
    // phase the session is in.
    const renderStatus = () => {
        switch (state) {
            case QA_STATES.IDLE:
                return (
                    <Status>
                        <StatusDot $bgColor="#3a3d44"><span role="img" aria-label="ready">🎯</span></StatusDot>
                        <StatusLabel>Ready to start</StatusLabel>
                    </Status>
                );

            case QA_STATES.COUNTDOWN:
                // The big 3-2-1 is CountdownOverlay over the whole stage, so the
                // number is deliberately not repeated here.
                return (
                    <Status>
                        <StatusDot $bgColor="#0e7490"><span role="img" aria-label="get ready">⏱️</span></StatusDot>
                        <StatusLabel $color="#00d9ff">Get ready…</StatusLabel>
                    </Status>
                );

            case QA_STATES.MURPHY_ASKING:
                return (
                    <Status>
                        <StatusDot $bgColor="#ffc107" $animate><span role="img" aria-label="speaking">🎤</span></StatusDot>
                        <StatusLabel $color="#ffc107">{participantName} is asking</StatusLabel>
                    </Status>
                );

            case QA_STATES.AWAITING_ANSWER:
                return (
                    <>
                        <Status>
                            <AudioWaves>
                                <AudioBar $delay={0} />
                                <AudioBar $delay={0.15} />
                                <AudioBar $delay={0.3} />
                                <AudioBar $delay={0.45} />
                            </AudioWaves>
                            <StatusLabel $color="#00ff88">Recording…</StatusLabel>
                        </Status>
                        {onNextQuestion && (
                            <NextQuestionButton
                                onClick={onNextQuestion}
                                data-testid="qa-next-question"
                                title="Click this (or press Enter) when you have finished answering"
                            >
                                {currentQuestion >= totalQuestions ? 'Answered, Finish' : 'Answered, Next Question →'}
                                <kbd>Enter</kbd>
                            </NextQuestionButton>
                        )}
                    </>
                );

            case QA_STATES.PROCESSING:
                return (
                    <Status>
                        <StatusDot $bgColor="#8a2be2" $animate><span role="img" aria-label="processing">⏳</span></StatusDot>
                        <StatusLabel $color="#b98aff">Processing…</StatusLabel>
                    </Status>
                );

            case QA_STATES.COMPLETE:
                return (
                    <Status>
                        <StatusDot $bgColor="#00ff88"><span role="img" aria-label="complete">✅</span></StatusDot>
                        <StatusLabel $color="#00ff88">
                            {totalQuestions - skippedCount} of {totalQuestions} answered
                        </StatusLabel>
                    </Status>
                );

            case QA_STATES.ERROR:
                return (
                    <Status>
                        <StatusDot $bgColor="#ff6b6b"><span role="img" aria-label="error">⚠️</span></StatusDot>
                        <StatusLabel $color="#ff6b6b">Something went wrong</StatusLabel>
                    </Status>
                );

            default:
                return null;
        }
    };

    return (
        <Container data-testid="qa-progress">
            <QuestionNumber>
                Q{currentQuestion}
                <TotalQuestions>/{totalQuestions}</TotalQuestions>
            </QuestionNumber>

            {/* Decorative while an answer is being recorded: it steps aside so
                "Recording..." and the advance button always fit the strip.
                The Q2/5 counter still shows where the session is. */}
            {state !== QA_STATES.AWAITING_ANSWER && (
                <ProgressBar data-testid="qa-progress-bar">
                    <ProgressFill $progress={progress} />
                </ProgressBar>
            )}

            {renderStatus()}

            {skippedCount > 0 && state !== QA_STATES.COMPLETE && (
                <SkippedBadge>{skippedCount} skipped</SkippedBadge>
            )}
        </Container>
    );
};

export default QAProgressIndicator;
